import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * A confirmation for something that cannot be taken back.
 *
 * `window.confirm` is the wrong instrument here, and not for taste: it gives
 * one sentence with no room to say what will survive, its buttons are named by
 * the browser rather than by the action, and it is dismissed by the same reflex
 * that dismisses everything else. Erasing an organization removes every
 * contact, template and document it holds. That deserves a sentence about what
 * stays, and a deliberate act to proceed.
 *
 * When `confirmWord` is set the action stays disabled until it is typed. That
 * is reserved for the irreversible ones — a member can be re-invited, a
 * contact cannot be un-erased.
 */
export function ConfirmSheet({
  title,
  children,
  confirmLabel,
  confirmWord,
  busy,
  error,
  onConfirm,
  onClose,
  cancelLabel = 'Cancel',
  recommend,
}: {
  title: string
  children: ReactNode
  confirmLabel: string
  cancelLabel?: string
  /**
   * For a pause that is a check rather than a loss. Proceeding destroys
   * nothing, so it is not red — but the sheet exists because going back is
   * usually right, so going back is the one that carries the weight.
   */
  recommend?: 'cancel'
  confirmWord?: string
  busy?: boolean
  error?: string
  onConfirm: () => void
  onClose: () => void
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const [typed, setTyped] = useState('')

  // Only showModal() puts the dialog in the top layer, traps focus and makes
  // the page behind it inert. Rendering the element does none of that. The
  // cleanup matters because StrictMode runs this twice in development and
  // showModal() throws on an already-open dialog.
  useEffect(() => {
    const el = ref.current
    el?.showModal()
    return () => el?.close()
  }, [])

  const ready = !busy && (!confirmWord || typed.trim() === confirmWord)

  return (
    <dialog className="sheet" ref={ref} aria-label={title} onClose={onClose}>
      <div className="sheet-body card">
        <h2>{title}</h2>
        {children}

        {confirmWord && (
          <div style={{ marginTop: '.9rem' }}>
            <label htmlFor="confirm-word">
              Type <strong>{confirmWord}</strong> to confirm
            </label>
            <input
              id="confirm-word"
              type="text"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </div>
        )}

        {error && <div className="notice notice-bad" style={{ marginTop: '.9rem' }}>{error}</div>}

        <div className="row" style={{ marginTop: '1.1rem', justifyContent: 'flex-end' }}>
          <button className={recommend === 'cancel' ? 'btn' : 'btn btn-secondary'} onClick={onClose} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={recommend === 'cancel' ? 'btn btn-secondary' : 'btn btn-danger'}
            onClick={onConfirm}
            disabled={!ready}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </dialog>
  )
}
